/**
 * Camera - Streamlined camera system for game engines
 * 
 * Consolidates CameraController, CameraManager, and CameraModeManager into
 * a single, easy-to-use camera system with support for multiple modes.
 * 
 * Uses Babylon.js best practices:
 * - UniversalCamera for FPS/Free modes (recommended by Babylon docs)
 * - ArcRotateCamera for Orbit/Follow modes
 * - FollowCamera for smooth third-person following
 * 
 * @example
 * ```typescript
 * const camera = new Camera(scene, canvas, { mode: "follow" });
 * camera.setFollowTarget(player.transformNode);
 * camera.setMode("orbit"); // Switch modes on the fly
 * ```
 */

import { Scene } from "@babylonjs/core/scene";
import { Camera as BabylonCamera } from "@babylonjs/core/Cameras/camera";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { UniversalCamera } from "@babylonjs/core/Cameras/universalCamera";
import { FollowCamera } from "@babylonjs/core/Cameras/followCamera";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Scalar } from "@babylonjs/core/Maths/math.scalar";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";

import { IDisposable, IUpdatable } from "../core/Engine.types";
import { EventEmitter } from "../core/EventEmitter";

// ============================================================================
// Types
// ============================================================================

/**
 * Available camera modes
 */
export type CameraMode = "free" | "orbit" | "follow" | "fps";

/**
 * Follow target types - flexible for different use cases
 */
export type FollowTarget = 
    | TransformNode 
    | AbstractMesh
    | (() => Vector3)
    | Vector3;

/**
 * Camera configuration
 */
export interface CameraConfig {
    /** Initial camera mode */
    mode?: CameraMode;
    /** Initial position */
    position?: Vector3;
    /** Initial target/look-at point */
    target?: Vector3;
    /** Field of view (radians) */
    fov?: number;
    /** Near clipping plane */
    minZ?: number;
    /** Far clipping plane */
    maxZ?: number;
}

/**
 * Mode-specific configuration
 */
export interface FreeModeConfig {
    /** Movement speed */
    speed?: number;
    /** Mouse sensitivity */
    sensitivity?: number;
    /** Enable sprint (shift key multiplier) */
    sprintMultiplier?: number;
    /** Inertia (0-1) */
    inertia?: number;
}

export interface OrbitModeConfig {
    /** Minimum orbit radius */
    minRadius?: number;
    /** Maximum orbit radius */
    maxRadius?: number;
    /** Minimum vertical angle (radians) */
    minBeta?: number;
    /** Maximum vertical angle (radians) */
    maxBeta?: number;
    /** Pan sensitivity (0 to disable) */
    panningSensibility?: number;
    /** Zoom wheel precision */
    wheelPrecision?: number;
    /** Enable auto-rotate */
    autoRotate?: boolean;
    /** Auto-rotate speed */
    autoRotateSpeed?: number;
}

export interface FollowModeConfig {
    /** Distance from target */
    distance?: number;
    /** Height above target */
    height?: number;
    /** Rotation offset (radians) */
    rotationOffset?: number;
    /** Camera acceleration */
    acceleration?: number;
    /** Maximum camera speed */
    maxSpeed?: number;
    /** Enable user rotation */
    enableRotation?: boolean;
}

export interface FPSModeConfig {
    /** Eye height offset */
    eyeHeight?: number;
    /** Mouse sensitivity */
    sensitivity?: number;
    /** Movement speed */
    speed?: number;
    /** Inertia */
    inertia?: number;
    /** Enable head bob */
    headBob?: boolean;
}

/**
 * Full configuration with all mode options
 */
/**
 * Combined configuration for Camera with mode-specific settings
 */
export interface CombinedCameraConfig extends CameraConfig {
    free?: FreeModeConfig;
    orbit?: OrbitModeConfig;
    follow?: FollowModeConfig;
    fps?: FPSModeConfig;
}

/**
 * Camera events
 */
export type CameraEvents = {
    modeChanged: { previous: CameraMode; current: CameraMode };
    targetChanged: { target: FollowTarget | null };
    positionChanged: { position: Vector3 };
};

// ============================================================================
// Default Configurations
// ============================================================================

const DEFAULT_CONFIG: Required<CameraConfig> = {
    mode: "orbit",
    position: new Vector3(0, 10, -20),
    target: Vector3.Zero(),
    fov: 0.8,
    minZ: 0.1,
    maxZ: 1000,
};

const DEFAULT_FREE: Required<FreeModeConfig> = {
    speed: 1.0,
    sensitivity: 0.002,
    sprintMultiplier: 2.0,
    inertia: 0.9,
};

const DEFAULT_ORBIT: Required<OrbitModeConfig> = {
    minRadius: 5,
    maxRadius: 200,
    minBeta: 0.1,
    maxBeta: Math.PI / 2 - 0.1,
    panningSensibility: 50,
    wheelPrecision: 10,
    autoRotate: false,
    autoRotateSpeed: 0.5,
};

const DEFAULT_FOLLOW: Required<FollowModeConfig> = {
    distance: 20,
    height: 10,
    rotationOffset: 0,
    acceleration: 0.05,
    maxSpeed: 20,
    enableRotation: true,
};

const DEFAULT_FPS: Required<FPSModeConfig> = {
    eyeHeight: 1.7,
    sensitivity: 0.002,
    speed: 1.0,
    inertia: 0.5,
    headBob: false,
};

// ============================================================================
// Camera Class
// ============================================================================

/**
 * Camera - Single camera system supporting multiple modes
 */
export class Camera extends EventEmitter<CameraEvents> implements IUpdatable, IDisposable {
    private _scene: Scene;
    private _canvas: HTMLCanvasElement;
    private _camera: BabylonCamera | null = null;
    private _currentMode: CameraMode;
    
    // Configuration
    private _config: Required<CameraConfig>;
    private _freeConfig: Required<FreeModeConfig>;
    private _orbitConfig: Required<OrbitModeConfig>;
    private _followConfig: Required<FollowModeConfig>;
    private _fpsConfig: Required<FPSModeConfig>;
    
    // Follow target
    private _followTarget: FollowTarget | null = null;
    private _followTargetNode: TransformNode | null = null;
    
    // State
    private _isDisposed: boolean = false;
    private _lastPosition: Vector3 = Vector3.Zero();

    // ========================================================================
    // Getters
    // ========================================================================

    /** Current Babylon.js camera instance */
    public get camera(): BabylonCamera | null {
        return this._camera;
    }

    /** Current camera mode */
    public get currentMode(): CameraMode {
        return this._currentMode;
    }

    /** Camera position */
    public get position(): Vector3 {
        return this._camera?.position ?? Vector3.Zero();
    }

    /** Is follow mode active with a target */
    public get isFollowing(): boolean {
        return this._currentMode === "follow" && this._followTarget !== null;
    }

    // ========================================================================
    // Constructor
    // ========================================================================

    constructor(
        scene: Scene,
        canvas: HTMLCanvasElement,
        config?: Partial<CombinedCameraConfig>
    ) {
        super();
        
        this._scene = scene;
        this._canvas = canvas;
        
        // Merge configurations with defaults
        this._config = { ...DEFAULT_CONFIG, ...config };
        this._freeConfig = { ...DEFAULT_FREE, ...config?.free };
        this._orbitConfig = { ...DEFAULT_ORBIT, ...config?.orbit };
        this._followConfig = { ...DEFAULT_FOLLOW, ...config?.follow };
        this._fpsConfig = { ...DEFAULT_FPS, ...config?.fps };
        
        // Set initial mode
        this._currentMode = this._config.mode;
        this._createCamera(this._currentMode);
    }

    // ========================================================================
    // Public API
    // ========================================================================

    /**
     * Switch camera mode
     */
    public setMode(mode: CameraMode): void {
        if (mode === this._currentMode || this._isDisposed) return;
        
        const previousMode = this._currentMode;
        
        // Store current state for transition
        const prevPosition = this._camera?.position.clone();
        let prevTarget: Vector3 | undefined;
        
        if (this._camera instanceof ArcRotateCamera) {
            prevTarget = this._camera.target.clone();
        }
        
        // Dispose old camera
        this._disposeCamera();
        
        // Create new camera
        this._currentMode = mode;
        this._createCamera(mode);
        
        // Restore reasonable position/target
        if (prevPosition && mode !== "fps") {
            if (this._camera instanceof ArcRotateCamera && prevTarget) {
                this._camera.target = prevTarget;
            }
        }
        
        // Emit event
        this.emit("modeChanged", { previous: previousMode, current: mode });
        
        console.log(`Camera: Mode changed from ${previousMode} to ${mode}`);
    }

    /**
     * Set follow target
     */
    public setFollowTarget(target: FollowTarget | null): void {
        this._followTarget = target;
        
        // If target is a TransformNode or Mesh, store reference
        if (target instanceof TransformNode) {
            this._followTargetNode = target;
        } else {
            this._followTargetNode = null;
        }
        
        // If using FollowCamera, set the lockedTarget (requires AbstractMesh)
        if (this._camera instanceof FollowCamera && this._followTargetNode) {
            // FollowCamera.lockedTarget requires AbstractMesh, not TransformNode
            // For TransformNode targets, we use ArcRotateCamera-based follow instead
        }
        
        this.emit("targetChanged", { target });
    }

    /**
     * Clear follow target
     */
    public clearFollowTarget(): void {
        this.setFollowTarget(null);
    }

    /**
     * Set camera position
     */
    public setPosition(position: Vector3): void {
        if (this._camera) {
            this._camera.position.copyFrom(position);
            this.emit("positionChanged", { position });
        }
    }

    /**
     * Set camera target/look-at point
     */
    public setTarget(target: Vector3): void {
        if (this._camera instanceof ArcRotateCamera) {
            this._camera.target = target.clone();
        } else if (this._camera instanceof UniversalCamera) {
            this._camera.setTarget(target.clone());
        } else if (this._camera instanceof FollowCamera) {
            // FollowCamera uses lockedTarget, not a Vector3 target
            // Position the camera looking at target
            this._camera.setTarget(target.clone());
        }
    }

    /**
     * Focus on a position with optional distance
     */
    public focusOn(position: Vector3, distance: number = 20): void {
        if (this._camera instanceof ArcRotateCamera) {
            this._camera.target = position.clone();
            this._camera.radius = distance;
        } else {
            // Calculate position behind target
            const direction = this._camera!.position.subtract(position).normalize();
            this.setPosition(position.add(direction.scale(distance)));
            this.setTarget(position);
        }
    }

    /**
     * Update mode-specific configuration
     */
    public configure<T extends CameraMode>(
        mode: T,
        config: T extends "free" ? Partial<FreeModeConfig> :
                T extends "orbit" ? Partial<OrbitModeConfig> :
                T extends "follow" ? Partial<FollowModeConfig> :
                Partial<FPSModeConfig>
    ): void {
        switch (mode) {
            case "free":
                Object.assign(this._freeConfig, config);
                break;
            case "orbit":
                Object.assign(this._orbitConfig, config);
                break;
            case "follow":
                Object.assign(this._followConfig, config);
                break;
            case "fps":
                Object.assign(this._fpsConfig, config);
                break;
        }
        
        // Re-apply if currently in this mode
        if (mode === this._currentMode) {
            this._applyModeConfig();
        }
    }

    /**
     * Get the resolved follow target position
     */
    public getFollowTargetPosition(): Vector3 | null {
        if (!this._followTarget) return null;
        
        if (this._followTarget instanceof TransformNode) {
            return this._followTarget.absolutePosition.clone();
        } else if (typeof this._followTarget === "function") {
            return this._followTarget();
        } else if (this._followTarget instanceof Vector3) {
            return this._followTarget.clone();
        }
        
        return null;
    }

    // ========================================================================
    // IUpdatable Implementation
    // ========================================================================

    /**
     * Update camera (call each frame)
     */
    public update(deltaTime: number): void {
        if (this._isDisposed || !this._camera) return;
        
        // Handle follow mode with manual target following
        if (this._currentMode === "follow" && this._followTarget) {
            this._updateFollowMode(deltaTime);
        }
        
        // Handle orbit auto-rotate
        if (this._currentMode === "orbit" && this._orbitConfig.autoRotate) {
            this._updateOrbitAutoRotate(deltaTime);
        }
        
        // Track position changes
        if (!this._camera.position.equals(this._lastPosition)) {
            this._lastPosition = this._camera.position.clone();
        }
    }

    // ========================================================================
    // IDisposable Implementation
    // ========================================================================

    /**
     * Dispose camera and resources
     */
    public dispose(): void {
        if (this._isDisposed) return;
        
        this._disposeCamera();
        this.disposeListeners();
        this._followTarget = null;
        this._followTargetNode = null;
        this._isDisposed = true;
        
        console.log("Camera: Disposed");
    }

    // ========================================================================
    // Private Methods
    // ========================================================================

    /**
     * Create camera for the specified mode
     */
    private _createCamera(mode: CameraMode): void {
        const { position, target, fov, minZ, maxZ } = this._config;
        
        switch (mode) {
            case "free":
                this._camera = this._createFreeCamera(position, target, fov, minZ, maxZ);
                break;
                
            case "orbit":
                this._camera = this._createOrbitCamera(position, target, fov, minZ, maxZ);
                break;
                
            case "follow":
                this._camera = this._createFollowCamera(position, target, fov, minZ, maxZ);
                break;
                
            case "fps":
                this._camera = this._createFPSCamera(position, fov, minZ, maxZ);
                break;
        }
        
        this._scene.activeCamera = this._camera;
    }

    /**
     * Create free movement camera (UniversalCamera recommended by Babylon.js)
     */
    private _createFreeCamera(
        position: Vector3,
        target: Vector3,
        fov: number,
        minZ: number,
        maxZ: number
    ): BabylonCamera {
        const config = this._freeConfig;
        const camera = new UniversalCamera("freeCamera", position, this._scene);
        
        camera.fov = fov;
        camera.minZ = minZ;
        camera.maxZ = maxZ;
        camera.speed = config.speed;
        camera.angularSensibility = 1 / config.sensitivity;
        camera.inertia = config.inertia;
        camera.setTarget(target);
        
        // WASD controls
        camera.keysUp = [87]; // W
        camera.keysDown = [83]; // S
        camera.keysLeft = [65]; // A
        camera.keysRight = [68]; // D
        camera.keysUpward = [69]; // E (up)
        camera.keysDownward = [81]; // Q (down)
        
        camera.attachControl(this._canvas, true);
        
        return camera;
    }

    /**
     * Create orbit camera (ArcRotateCamera)
     */
    private _createOrbitCamera(
        position: Vector3,
        target: Vector3,
        fov: number,
        minZ: number,
        maxZ: number
    ): BabylonCamera {
        const config = this._orbitConfig;
        
        // Calculate initial alpha/beta/radius from position
        const radius = Vector3.Distance(position, target);
        
        const camera = new ArcRotateCamera(
            "orbitCamera",
            Math.PI / 2, // alpha
            Math.PI / 3, // beta
            radius,
            target,
            this._scene
        );
        
        camera.fov = fov;
        camera.minZ = minZ;
        camera.maxZ = maxZ;
        camera.lowerRadiusLimit = config.minRadius;
        camera.upperRadiusLimit = config.maxRadius;
        camera.lowerBetaLimit = config.minBeta;
        camera.upperBetaLimit = config.maxBeta;
        camera.panningSensibility = config.panningSensibility;
        camera.wheelPrecision = config.wheelPrecision;
        
        camera.attachControl(this._canvas, true);
        
        return camera;
    }

    /**
     * Create follow camera
     * Uses ArcRotateCamera for smoother following with user control
     */
    private _createFollowCamera(
        _position: Vector3, // Not used - follow camera positions based on target
        target: Vector3,
        fov: number,
        minZ: number,
        maxZ: number
    ): BabylonCamera {
        const config = this._followConfig;
        
        // Use ArcRotateCamera for follow - allows user rotation while following
        const camera = new ArcRotateCamera(
            "followCamera",
            Math.PI / 2,
            Math.PI / 4,
            config.distance,
            target,
            this._scene
        );
        
        camera.fov = fov;
        camera.minZ = minZ;
        camera.maxZ = maxZ;
        camera.lowerRadiusLimit = config.distance * 0.5;
        camera.upperRadiusLimit = config.distance * 2;
        camera.lowerBetaLimit = 0.1;
        camera.upperBetaLimit = Math.PI / 2 - 0.1;
        camera.wheelPrecision = 10;
        
        if (config.enableRotation) {
            camera.attachControl(this._canvas, true);
        }
        
        return camera;
    }

    /**
     * Create FPS camera
     */
    private _createFPSCamera(
        initialPosition: Vector3,
        fov: number,
        minZ: number,
        maxZ: number
    ): BabylonCamera {
        const config = this._fpsConfig;
        const camera = new UniversalCamera("fpsCamera", initialPosition, this._scene);
        
        camera.fov = fov;
        camera.minZ = minZ;
        camera.maxZ = maxZ;
        camera.speed = config.speed;
        camera.angularSensibility = 1 / config.sensitivity;
        camera.inertia = config.inertia;
        
        // WASD controls
        camera.keysUp = [87];
        camera.keysDown = [83];
        camera.keysLeft = [65];
        camera.keysRight = [68];
        
        camera.attachControl(this._canvas, true);
        
        return camera;
    }

    /**
     * Apply current mode configuration to camera
     */
    private _applyModeConfig(): void {
        if (!this._camera) return;
        
        switch (this._currentMode) {
            case "free":
                if (this._camera instanceof UniversalCamera) {
                    this._camera.speed = this._freeConfig.speed;
                    this._camera.angularSensibility = 1 / this._freeConfig.sensitivity;
                    this._camera.inertia = this._freeConfig.inertia;
                }
                break;
                
            case "orbit":
                if (this._camera instanceof ArcRotateCamera) {
                    this._camera.lowerRadiusLimit = this._orbitConfig.minRadius;
                    this._camera.upperRadiusLimit = this._orbitConfig.maxRadius;
                    this._camera.lowerBetaLimit = this._orbitConfig.minBeta;
                    this._camera.upperBetaLimit = this._orbitConfig.maxBeta;
                    this._camera.panningSensibility = this._orbitConfig.panningSensibility;
                    this._camera.wheelPrecision = this._orbitConfig.wheelPrecision;
                }
                break;
                
            case "fps":
                if (this._camera instanceof UniversalCamera) {
                    this._camera.speed = this._fpsConfig.speed;
                    this._camera.angularSensibility = 1 / this._fpsConfig.sensitivity;
                    this._camera.inertia = this._fpsConfig.inertia;
                }
                break;
        }
    }

    /**
     * Update follow mode - smooth camera following
     */
    private _updateFollowMode(deltaTime: number): void {
        const targetPos = this.getFollowTargetPosition();
        if (!targetPos || !(this._camera instanceof ArcRotateCamera)) return;
        
        const config = this._followConfig;
        
        // Smooth follow using lerp
        const lerpFactor = Scalar.Clamp(deltaTime * config.acceleration * 100, 0, 1);
        this._camera.target = Vector3.Lerp(
            this._camera.target,
            targetPos,
            lerpFactor
        );
    }

    /**
     * Update orbit auto-rotate
     */
    private _updateOrbitAutoRotate(deltaTime: number): void {
        if (!(this._camera instanceof ArcRotateCamera)) return;
        
        this._camera.alpha += this._orbitConfig.autoRotateSpeed * deltaTime;
    }

    /**
     * Dispose current camera
     */
    private _disposeCamera(): void {
        if (this._camera) {
            this._camera.detachControl();
            this._camera.dispose();
            this._camera = null;
        }
    }
}
